import { combineReducers } from "redux"

import { entityTypeFromIModelOrIRI } from "@api/entityTypeEndpointDefinitions"
import {
  ICategory,
  IDiscussion,
  IFeedbackInvitation,
  IFeedbackPost,
  IIdea,
  INumericIdentifierModel,
  IProgram,
  IProject,
  IProjectFollowership,
  IProvider,
  ISupportRequest,
  ITeamUpload,
  IUser,
  IUserObjectRole,
} from "@api/schema"
import { IActionRequest } from "@api/schema/action-requests"
import { IAttachmentDefinition, IChallenge, IProposal } from "@api/schema/challenges"
import { IChapter, IDocument } from "@modules/documents/src"
import { IFinancePlan } from "@modules/financePlan/models/financePlan"
import { ActionTypes, EntityType, StoredEntityType } from "@redux/common/reduxTypes"

import {
  IEntityApiRequestSuccessAction,
  ILoadCollectionSuccessAction,
  IModelCLUDOperationSuccessAction
} from "./actions"

/*
 * defines an ObjectReducer (for the specific data of entities)
 */

export interface IIndexedCollectionState<T extends INumericIdentifierModel> {
  [id: number]: T
}

export const initialIndexedCollectionState: IIndexedCollectionState<INumericIdentifierModel> = {}


/**
 * This Reducer is a general Reducer for all Entity-Types and collections of those Entity-Types, when requesting
 * data of them from the API.
 *
 * It only handles model-SUCCESS-Actions, triggered by the (general)saga(s), after the request to the API was
 * successfull and the (new/updated) or loaded entity should be stored in the state or should be deleted from
 * the state. It does not handle request-success-actions!
 *
 * Is used in redux/reducer/data.ts
 *
 * @param entityTypes The EntityType or the EntityTypes to be handled. Is a "rest parameter", b/c the scopedObjectReducer can handle 1 to n EntityTypes.
 * @returns An updated Entity or an updated collection of those Entities after Update, Delete or collection-loading
 */
export const scopedObjectReducer =
  <T extends INumericIdentifierModel>(...entityTypes: StoredEntityType[]): (
    state: IIndexedCollectionState<T>,
    action: IEntityApiRequestSuccessAction<StoredEntityType>
  ) => IIndexedCollectionState<T> => {
    const objectReducer = (
      state: IIndexedCollectionState<T> = initialIndexedCollectionState as IIndexedCollectionState<T>,
      action: IEntityApiRequestSuccessAction<StoredEntityType>,
    ) => {
      // first check if we're responsible; if action has an other entityType than what we're supposed to react on, skip
      if (!entityTypes.includes(action.entityType)) {
        return state
      }

      switch (action.type) {
        case ActionTypes.LoadSuccess:
        // no break
        case ActionTypes.CreateSuccess:
        // no break
        case ActionTypes.UpdateSuccess:
          const singleModel = (action as IModelCLUDOperationSuccessAction<T>).model

          // If the actual manipulated model is NOT part of this reducer's designated `entityType`s, skip,
          // because otherwise, different sub-class instances of the abstract super class would be included in the state.
          if (!entityTypes.includes(entityTypeFromIModelOrIRI(singleModel["@id"]))) {
            return state
          }

          return { ...state, [singleModel.id]: singleModel }

        case ActionTypes.DeleteSuccess:
          const deletedModel = (action as IModelCLUDOperationSuccessAction<T>).model

          // If the actual manipulated model is NOT part of this reducer's designated `entityType`s, skip,
          // because otherwise, different sub-class instances of the abstract super class would be included in the state.
          if (!entityTypes.includes(entityTypeFromIModelOrIRI(deletedModel["@id"]))) {
            return state
          }

          const reduced: IIndexedCollectionState<T> = {}
          Object.values(state).forEach((value: T) => {
            if (value.id === deletedModel.id) { return }
            reduced[value.id] = value
          })
          return reduced

        case ActionTypes.LoadCollectionSuccess:
          const newCollection: IIndexedCollectionState<T> = {}
          const data = (action as ILoadCollectionSuccessAction<T>).collection
          data.member.forEach((element: T) => {

            // If the actual manipulated model IS part of this reducer's designated `entityType`s, use it,
            // because otherwise, different sub-class instances of the abstract super class would be included in the state.
            if (entityTypes.includes(entityTypeFromIModelOrIRI(element["@id"]))) {
              newCollection[element.id] = element
            }
          })
          return { ...state, ...newCollection }

        // necessary, because all reducers are called
        // => if an action is triggered, where this reducer is not responsible, ignore this action and return the previous state
        default:
          return state
      }
    }

    return objectReducer
  }

/**
 * This type lists all StoredEntityType and ensures that there will be a data reducer for each of them.
 */
type DataStateType<T> = {
  [key in StoredEntityType]: (state: IIndexedCollectionState<T>, action: IEntityApiRequestSuccessAction<StoredEntityType>) => IIndexedCollectionState<T>
}
/**
 * Create object reducers for all entity types to prevent errors from typescript,
 * even if we don't need the reducer and cannot replace it with a fake reducer like (state) => state
 * NOTE the fields in this reducer are named EXACTLY like the value in EntityType.* according to the type parameter of scopedObjectReducer
 */
export const scopedObjectsCombinedReducer = combineReducers<DataStateType<INumericIdentifierModel>>({
  [EntityType.ActionRequest]: scopedObjectReducer<IActionRequest>(EntityType.ActionRequest),
  [EntityType.AttachmentDefinition]: scopedObjectReducer<IAttachmentDefinition>(EntityType.AttachmentDefinition),
  [EntityType.Category]: scopedObjectReducer<ICategory>(EntityType.Category),

  // Reducers for "challenge" class hierarchy.
  // One reducer for each sub type, and another one for the "merged" super type.
  // This is necessary, because merged BasicChallenges+Funds are required for a global marketplace page,
  // and there is a distinct endpoint to get their collection.
  // @see also doc/redux.md
  // TODO: refactor: create a class-hierarchy aware reducer that handles stuff w/o duplicates, see FCP-2045
  [EntityType.AbstractChallenge]: scopedObjectReducer<IChallenge>(EntityType.AbstractChallenge, EntityType.BasicChallenge, EntityType.Fund),
  [EntityType.BasicChallenge]: scopedObjectReducer<IChallenge>(EntityType.AbstractChallenge, EntityType.BasicChallenge),
  [EntityType.Fund]: scopedObjectReducer<IChallenge>(EntityType.AbstractChallenge, EntityType.Fund),

  [EntityType.Discussion]: scopedObjectReducer<IDiscussion>(EntityType.Discussion),
  [EntityType.ProjectDocument]: scopedObjectReducer<IDocument>(EntityType.ProjectDocument),
  [EntityType.Chapter]: scopedObjectReducer<IChapter>(EntityType.Chapter),
  [EntityType.FinancePlan]: scopedObjectReducer<IFinancePlan>(EntityType.FinancePlan),

  [EntityType.Idea]: scopedObjectReducer<IIdea>(EntityType.Idea),
  [EntityType.FeedbackInvitation]: scopedObjectReducer<IFeedbackInvitation>(EntityType.FeedbackInvitation),
  [EntityType.FeedbackPost]: scopedObjectReducer<IFeedbackPost>(EntityType.FeedbackPost),
  [EntityType.Program]: scopedObjectReducer<IProgram>(EntityType.Program),
  [EntityType.Project]: scopedObjectReducer<IProject>(EntityType.Project),
  [EntityType.ProjectFollowership]: scopedObjectReducer<IProjectFollowership>(EntityType.ProjectFollowership),

  // Reducers for "proposal" class hierarchy.
  // One reducer for each sub type, and another one for the "merged" super type.
  // This is necessary, because merged BasicProposals+FundApplications could be shown on a project's "our applications" page,
  // and there is a distinct endpoint to get their collection.
  // @see also doc/redux.md
  // TODO: refactor: create a class-hierarchy aware reducer that handles stuff w/o duplicates, see FCP-2045
  [EntityType.AbstractProposal]: scopedObjectReducer<IProposal>(EntityType.AbstractProposal, EntityType.BasicProposal, EntityType.FundApplication),
  [EntityType.BasicProposal]: scopedObjectReducer<IProposal>(EntityType.AbstractProposal, EntityType.BasicProposal),
  [EntityType.FundApplication]: scopedObjectReducer<IProposal>(EntityType.AbstractProposal, EntityType.FundApplication),

  // Reducers for "proposalAttachment" class hierarchy.
  // @see doc/redux.md
  [EntityType.AbstractProposalAttachment]: scopedObjectReducer<IProposal>(
    EntityType.AbstractProposalAttachment,
    EntityType.DocumentProposalAttachment,
    EntityType.FileProposalAttachment,
    EntityType.ProjectPlanProposalAttachment
  ),
  [EntityType.DocumentProposalAttachment]: scopedObjectReducer<IProposal>(EntityType.AbstractProposalAttachment, EntityType.DocumentProposalAttachment),
  [EntityType.FileProposalAttachment]: scopedObjectReducer<IProposal>(EntityType.AbstractProposalAttachment, EntityType.FileProposalAttachment),
  [EntityType.ProjectPlanProposalAttachment]: scopedObjectReducer<IProposal>(EntityType.AbstractProposalAttachment, EntityType.ProjectPlanProposalAttachment),

  [EntityType.Provider]: scopedObjectReducer<IProvider>(EntityType.Provider),
  [EntityType.SupportRequest]: scopedObjectReducer<ISupportRequest>(EntityType.SupportRequest),
  [EntityType.TeamUpload]: scopedObjectReducer<ITeamUpload>(EntityType.TeamUpload),
  [EntityType.User]: scopedObjectReducer<IUser>(EntityType.User),
  [EntityType.UserObjectRole]: scopedObjectReducer<IUserObjectRole>(EntityType.UserObjectRole),
})
